import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { URLService } from './dynamic-url.service';

/**
 * Institution connectors — the external services the agent can act against
 * (CONNECTOR_ARCHITECTURE.md).
 *
 * Every call goes to `/tutor/institution/connectors`, which is the INSTITUTION-admin
 * surface (as opposed to `/admin/*`, which is for system administrators). The
 * institution is resolved server-side from the caller, never sent from here — so
 * this service cannot be pointed at somebody else's institution.
 *
 * No credential ever crosses this boundary: the API returns names, status and
 * provenance only. Tokens live server-side behind `select: false` and are handed
 * straight to the MCP transport at call time.
 */

export type ConnectorStatus = 'available' | 'connected' | 'needs_reauth';

export interface Connector {
  key: string;
  /** §6 — identifies ONE connected account of this key. Absent on connectors made
   *  before multi-instance, which is why every read still works without it. */
  instanceId?: string;
  label: string;
  mcpUrl: string;
  status: ConnectorStatus;
  usableBy?: string;
  scopes?: string[];
  connectedAccount?: string;
  connectedByName?: string;
  connectedAt?: string;
  lastUsedAt?: string;
  lastError?: string;
}

/** A service we have vetted, plus how much setup it needs. */
export interface CatalogEntry {
  key: string;
  label: string;
  /** Absent for a `direct` entry, which has no MCP server to point at. */
  mcpUrl?: string;
  /** `mcp` (default) or `direct`. A direct entry is a provider we already have
   *  first-class support for — the connector only holds its credential — so there
   *  is nothing to probe and the discovery step is skipped entirely. */
  kind?: string;
  /** Definitive for a direct entry (nothing advertises them); empty for MCP, where
   *  the provider reports them during discovery. */
  scopes?: string[];
  /** Absolute URL of the service's icon, when we have one. Absent = draw the
   *  service's initial instead — the normal case for ~15% (no discoverable
   *  favicon, or a PaaS-hosted server whose favicon is the platform's logo). */
  iconUrl?: string;
  blurb: string;
  /** one_click | needs_allowlist | manual_client | gated */
  setupPath: string;
  /** True only where a consent flow has been completed end to end for real. */
  verified?: boolean;
  /** §6 — several accounts of this service may be connected (support@, security@…).
   *  When true, "already connected" stops meaning "done" and starts meaning
   *  "connected at least once". */
  multiInstance?: boolean;
  /** Shown before the admin starts, when setup is not one-click. */
  setupNote?: string;
  alreadyConnected?: boolean;
}

/** What `discover` learned by probing the URL, before anything is stored. */
export interface ConnectorDiscovery {
  authorizationEndpoint?: string;
  tokenEndpoint?: string;
  registrationEndpoint?: string;
  scopes?: string[];
  issuer?: string;
  /** `unknown` means a registration endpoint exists but whether it accepts an
   *  anonymous client is only knowable by trying — OAuth metadata has no field
   *  for it. */
  dcr: 'open' | 'gated' | 'none' | 'unknown';
  problem?: string;
}

@Injectable({ providedIn: 'root' })
export class ConnectorsService {
  constructor(
    private http: HttpClient,
    private urlService: URLService,
  ) {}

  private base(): string {
    return `${this.urlService.getDynamicUrl()}/tutor/institution/connectors`;
  }

  /** The vetted services, with the ones this institution already has marked. */
  catalog(): Observable<{ catalog: CatalogEntry[] }> {
    return this.http.get<{ catalog: CatalogEntry[] }>(`${this.base()}/catalog`);
  }

  list(): Observable<{ connectors: Connector[] }> {
    return this.http.get<{ connectors: Connector[] }>(this.base());
  }

  /** Probe a pasted MCP URL. Persists nothing — safe to run on whatever was typed. */
  discover(mcpUrl: string): Observable<{ discovery: ConnectorDiscovery }> {
    return this.http.post<{ discovery: ConnectorDiscovery }>(`${this.base()}/discover`, {
      mcpUrl,
    });
  }

  /**
   * Create the connector. `clientId`/`clientSecret` are only needed for providers
   * that do not support dynamic client registration — for the rest the server
   * registers us automatically and the admin supplies nothing.
   */
  create(body: {
    key?: string;
    label?: string;
    mcpUrl?: string;
    /** Pick from the catalog — supplies key, label and URL server-side. */
    catalogKey?: string;
    clientId?: string;
    clientSecret?: string;
    /** For `api_key` services — the long-lived key the admin pasted. Sent once at
     *  create; never returned by any read, since it is stored `select: false`. */
    apiKey?: string;
  }): Observable<{ connector: Connector }> {
    return this.http.post<{ connector: Connector }>(this.base(), body);
  }

  /**
   * Begin consent. Returns the provider URL to send the admin to; the caller opens
   * it rather than this service redirecting, so the account warning can be shown
   * first — the flow authorizes whoever is signed in to the provider in that
   * browser, which is how a personal account silently becomes an org credential.
   */
  authorize(
    key: string,
    /** §6 — WHICH connection to send through consent. The API refuses to guess when
     *  a key has several, and guessing here would walk an admin through Google's
     *  consent screen for a mailbox they did not pick. */
     instanceId?: string
  ): Observable<{ authorizationUrl: string }> {
    return this.http.post<{ authorizationUrl: string }>(
      `${this.base()}/${encodeURIComponent(key)}/authorize${ 
        instanceId ? `?instanceId=${encodeURIComponent(instanceId)}` : ''}`,
      {},
    );
  }

  /** §6 — deletes ONE connection. Without instanceId the API refuses when a key
   *  holds several, rather than pulling them all (which once destroyed a live
   *  credential). */
  remove(key: string, instanceId?: string): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(
      `${this.base()}/${encodeURIComponent(key)}${
        instanceId ? `?instanceId=${encodeURIComponent(instanceId)}` : ''
      }`,
    );
  }
}
